import React, { useLayoutEffect, useRef } from 'react';
import { Paper, TableContainer } from '@mui/material';

/**
 * Drop-in csere a `<TableContainer component={Paper} variant="outlined">` helyett
 * az admin táblákhoz, hogy egy sor adatai és gombjai se igényeljenek vízszintes
 * görgetést:
 *
 * - `stackBelow` (alap: 'md') alatt a táblázat kártyalistává alakul: minden sor
 *   egy keretes blokk, minden cella egy "fejléc: érték" sor. A fejléc-feliratokat
 *   a komponens maga másolja a cellák `data-label` attribútumába a `<thead>`
 *   alapján, így a meglévő táblákon nem kell cellánként semmit módosítani.
 *   Azok a sorok, amelyek cellaszáma eltér a fejlécétől (pl. colSpan-es üres
 *   állapot), felirat nélkül, középre igazítva jelennek meg.
 * - `stickyLastColumn` esetén e fölött az utolsó (Műveletek) oszlop jobbra
 *   tapad, így a gombok akkor is látszanak, ha a táblázat mégis szélesebb.
 *
 * Minden más prop a `TableContainer`-é; `component` nélkül outlined `Paper`.
 */
const ResponsiveTableContainer = ({
  children,
  stackBelow = 'md',
  stickyLastColumn = false,
  component,
  sx,
  ...props
}) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const applyLabels = () => {
      const heads = [...root.querySelectorAll('thead tr:last-child th')].map((th) => th.textContent.trim());
      root.querySelectorAll('tbody tr').forEach((tr) => {
        const cells = [...tr.children];
        if (cells.length !== heads.length) return;
        cells.forEach((td, i) => {
          if (td.getAttribute('data-label') !== heads[i]) td.setAttribute('data-label', heads[i]);
        });
      });
    };
    applyLabels();
    const observer = new MutationObserver(applyLabels);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const containerProps = component ? { component } : { component: Paper, variant: 'outlined' };

  return (
    <TableContainer
      ref={ref}
      {...containerProps}
      {...props}
      sx={[
        (theme) => ({
          [theme.breakpoints.down(stackBelow)]: {
            border: 'none',
            boxShadow: 'none',
            bgcolor: 'transparent',
            overflowX: 'visible',
            maxHeight: 'none',
            '& table, & tbody': { display: 'block', width: '100%' },
            '& thead': { display: 'none' },
            '& tbody tr': {
              display: 'block',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              mb: 1.5,
              px: 2,
              py: 1,
            },
            '& tbody td': {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              minWidth: 0,
              px: 0,
              py: 0.75,
              border: 'none',
              textAlign: 'right !important',
              overflowWrap: 'anywhere',
            },
            '& tbody td[data-label]::before': {
              content: 'attr(data-label)',
              ...theme.typography.caption,
              color: theme.palette.text.secondary,
              textAlign: 'left',
              flexShrink: 0,
              maxWidth: '45%',
            },
            '& tbody td:not([data-label])': { display: 'block', textAlign: 'center !important' },
          },
          ...(stickyLastColumn && {
            [theme.breakpoints.up(stackBelow)]: {
              '& th:last-of-type, & td:last-of-type': {
                position: 'sticky',
                right: 0,
                zIndex: 1,
                bgcolor: 'background.paper',
                boxShadow: '-8px 0 8px -8px rgba(0,0,0,0.2)',
              },
            },
          }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </TableContainer>
  );
};

export default ResponsiveTableContainer;
